import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  actualizarEmpresa,
  crearEmpresa,
  listarEmpresas,
  readinessEmpresa,
} from "@/lib/api";
import type { PerfilEmpresa, ReadinessEmpresa } from "@/lib/api";
import { Header } from "@/components/Header";
import { Dialog } from "@/components/ui/Dialog";
import { extractApiError } from "@/lib/utils";

/** Estado del formulario de perfil (create y edit comparten campos). Todos
 *  requeridos (FORM_VACIO da defaults); `jurisdiccion` es texto separado por comas. */
interface FormPerfil {
  codigo: string;
  nombre_legal: string;
  nit: string;
  codigo_operador: string;
  jurisdiccion: string;
  umbral_dias_alerta_sap: number;
  umbral_dias_legal_sap: number;
  plantilla_default: string;
  plantilla_accede: string;
  plantilla_no_accede: string;
  plantilla_improcedente: string;
  nic_regex: string;
  soporte_email: string;
  web: string;
  corpus_namespace: string;
}

const FORM_VACIO: FormPerfil = {
  codigo: "",
  nombre_legal: "",
  nit: "",
  codigo_operador: "",
  jurisdiccion: "",
  umbral_dias_alerta_sap: 13,
  umbral_dias_legal_sap: 15,
  plantilla_default: "",
  plantilla_accede: "",
  plantilla_no_accede: "",
  plantilla_improcedente: "",
  nic_regex: "^\\d{4,12}$",
  soporte_email: "",
  web: "",
  corpus_namespace: "",
};

function aFormPerfil(p: PerfilEmpresa): FormPerfil {
  return { ...p, codigo: p.empresa, jurisdiccion: p.jurisdiccion.join(", ") };
}

function jurisdiccionArray(texto: string): string[] {
  return texto
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Administración de empresas (tenants) — sólo administradores. */
export function EmpresasPage() {
  const qc = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<PerfilEmpresa | null>(null);
  const [readiness, setReadiness] = useState<string | null>(null);

  const { data: empresas, isLoading } = useQuery({
    queryKey: ["empresas"],
    queryFn: listarEmpresas,
  });

  const refrescar = () => qc.invalidateQueries({ queryKey: ["empresas"] });

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="page page--wide">
        <div className="crumbs">
          <Link to="/">Bandeja</Link>
          <span className="sep">/</span>
          <span>Empresas</span>
        </div>

        <div className="page-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Administración · Tenants
            </div>
            <h1>Empresas</h1>
            <p className="sub">
              Operadores (tenants) que atiende GEMA. Crea y configura su perfil —
              razón social, umbrales SAP, plantillas y corpus. «Estado» indica qué
              le falta a cada empresa para operar con identidad propia.
            </p>
          </div>
          <button className="btn btn--primary" onClick={() => setCreando(true)}>
            + Nueva empresa
          </button>
        </div>

        <div className="card">
          <div className="card__body" style={{ padding: 0 }}>
            {isLoading ? (
              <p className="muted" style={{ padding: 16 }}>Cargando…</p>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Razón social</th>
                    <th>Jurisdicción</th>
                    <th>Umbral SAP</th>
                    <th style={{ textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(empresas ?? []).map((e) => (
                    <tr key={e.empresa}>
                      <td className="mono">{e.empresa}</td>
                      <td>{e.nombre_legal}</td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {e.jurisdiccion.join(", ") || "—"}
                      </td>
                      <td className="mono">
                        {e.umbral_dias_alerta_sap} / {e.umbral_dias_legal_sap} d
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setReadiness(e.empresa)}
                        >
                          Estado
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setEditando(e)}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {creando && (
        <ModalCrear onCerrar={() => setCreando(false)} onOk={refrescar} />
      )}
      {editando && (
        <ModalEditar
          empresa={editando}
          onCerrar={() => setEditando(null)}
          onOk={refrescar}
        />
      )}
      {readiness && (
        <ModalReadiness codigo={readiness} onCerrar={() => setReadiness(null)} />
      )}
    </div>
  );
}

// ─── Modal: crear ──────────────────────────────────────────────────── //

function ModalCrear({ onCerrar, onOk }: { onCerrar: () => void; onOk: () => void }) {
  const [form, setForm] = useState<FormPerfil>(FORM_VACIO);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      crearEmpresa({ ...form, jurisdiccion: jurisdiccionArray(form.jurisdiccion) }),
    onSuccess: () => {
      onOk();
      onCerrar();
    },
    onError: (e) => setError(extractApiError(e, "No se pudo crear la empresa.")),
  });

  const listo =
    form.codigo.trim().length >= 2 &&
    form.nombre_legal.trim().length > 0 &&
    form.corpus_namespace.trim().length > 0 &&
    !mut.isPending;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mut.mutate();
  }

  return (
    <Dialog
      abierto
      titulo="Nueva empresa"
      onCerrar={onCerrar}
      acciones={
        <>
          <button className="btn btn--ghost" onClick={onCerrar}>Cancelar</button>
          <button className="btn btn--primary" onClick={enviar} disabled={!listo}>
            {mut.isPending ? "Creando…" : "Crear"}
          </button>
        </>
      }
    >
      <form onSubmit={enviar} className="stack stack-3">
        <div className="field">
          <label>Código de la empresa</label>
          <input
            className="input mono"
            value={form.codigo}
            onChange={(e) => {
              const codigo = e.target.value.toUpperCase().replace(/\s/g, "");
              // Autocompleta los identificadores técnicos desde el código,
              // para que el funcional no tenga que escribirlos.
              setForm({
                ...form,
                codigo,
                corpus_namespace: codigo ? `ccu_${codigo.toLowerCase()}` : "",
                plantilla_default: codigo ? `${codigo}_PLANT_GENERICA` : "",
                plantilla_accede: codigo ? `${codigo}_PLANT_ACCEDE` : "",
                plantilla_no_accede: codigo ? `${codigo}_PLANT_NO_ACCEDE` : "",
                plantilla_improcedente: codigo ? `${codigo}_PLANT_IMPROCEDENTE` : "",
              });
            }}
            placeholder="EPM"
            autoComplete="off"
          />
          <span className="hint">
            Identificador corto y único del operador (ej. AFINIA, AIR_E, EPM).
          </span>
        </div>
        <CamposPerfil form={form} setForm={setForm} />
        {error && <BannerError texto={error} />}
      </form>
    </Dialog>
  );
}

// ─── Modal: editar ─────────────────────────────────────────────────── //

function ModalEditar({
  empresa,
  onCerrar,
  onOk,
}: {
  empresa: PerfilEmpresa;
  onCerrar: () => void;
  onOk: () => void;
}) {
  const [form, setForm] = useState<FormPerfil>(aFormPerfil(empresa));
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      actualizarEmpresa(empresa.empresa, {
        nombre_legal: form.nombre_legal,
        nit: form.nit,
        codigo_operador: form.codigo_operador,
        jurisdiccion: jurisdiccionArray(form.jurisdiccion),
        umbral_dias_alerta_sap: form.umbral_dias_alerta_sap,
        umbral_dias_legal_sap: form.umbral_dias_legal_sap,
        plantilla_default: form.plantilla_default,
        plantilla_accede: form.plantilla_accede,
        plantilla_no_accede: form.plantilla_no_accede,
        plantilla_improcedente: form.plantilla_improcedente,
        nic_regex: form.nic_regex,
        soporte_email: form.soporte_email,
        web: form.web,
        corpus_namespace: form.corpus_namespace,
      }),
    onSuccess: () => {
      onOk();
      onCerrar();
    },
    onError: (e) => setError(extractApiError(e, "No se pudo actualizar.")),
  });

  return (
    <Dialog
      abierto
      titulo={`Editar ${empresa.empresa}`}
      onCerrar={onCerrar}
      acciones={
        <>
          <button className="btn btn--ghost" onClick={onCerrar}>Cancelar</button>
          <button
            className="btn btn--primary"
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <div className="stack stack-3">
        <CamposPerfil form={form} setForm={setForm} />
        {error && <BannerError texto={error} />}
      </div>
    </Dialog>
  );
}

// ─── Modal: readiness ──────────────────────────────────────────────── //

function ModalReadiness({ codigo, onCerrar }: { codigo: string; onCerrar: () => void }) {
  const { data, isLoading } = useQuery<ReadinessEmpresa>({
    queryKey: ["empresa-readiness", codigo],
    queryFn: () => readinessEmpresa(codigo),
  });

  return (
    <Dialog
      abierto
      titulo={`Estado operativo — ${codigo}`}
      onCerrar={onCerrar}
      acciones={<button className="btn btn--primary" onClick={onCerrar}>Cerrar</button>}
    >
      {isLoading || !data ? (
        <p className="muted">Consultando…</p>
      ) : (
        <div className="stack stack-3">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge badge--green">Operativa (baseline)</span>
            {data.plantillas_branded ? (
              <span className="badge badge--green">Plantillas propias</span>
            ) : (
              <span className="badge badge--amber">Carta LLM + DOCX genérico</span>
            )}
            {data.corpus_propio_archivos.length > 0 ? (
              <span className="badge badge--green">
                Corpus propio ({data.corpus_propio_archivos.length})
              </span>
            ) : (
              <span className="badge badge--amber">Sin CCU propio</span>
            )}
          </div>
          {data.faltantes.length > 0 ? (
            <div>
              <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Pendiente:</p>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {data.faltantes.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              La empresa está completamente configurada.
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

// ─── Campos compartidos del perfil ─────────────────────────────────── //

// Patrón NIC "simple": ^\d{min,max}$ — el caso común (validar largo en dígitos).
const RE_NIC_SIMPLE = /^\^\\d\{(\d+),(\d+)\}\$$/;

/** Campo NIC amigable: "de X a Y dígitos" en vez de una regex cruda. */
function NicField({
  valor,
  onChange,
}: {
  valor: string;
  onChange: (v: string) => void;
}) {
  const m = valor.match(RE_NIC_SIMPLE);
  const [avanzado, setAvanzado] = useState(!m);

  if (avanzado) {
    return (
      <div className="field">
        <label>Formato del NIC — patrón (avanzado)</label>
        <input
          className="input mono"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
        />
        <span className="hint">
          Expresión regular. Para el caso normal,{" "}
          <a role="button" style={{ cursor: "pointer" }} onClick={() => setAvanzado(false)}>
            usar cantidad de dígitos
          </a>
          .
        </span>
      </div>
    );
  }

  const min = m ? m[1] : "6";
  const max = m ? m[2] : "10";
  const actualizar = (nmin: string, nmax: string) =>
    onChange(`^\\d{${nmin || "0"},${nmax || "0"}}$`);

  return (
    <div className="field">
      <label>Formato del NIC (número de identificación del contrato)</label>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 13 }}>de</span>
        <input
          className="input"
          type="number"
          style={{ width: 90 }}
          value={min}
          onChange={(e) => actualizar(e.target.value, max)}
        />
        <span className="muted" style={{ fontSize: 13 }}>a</span>
        <input
          className="input"
          type="number"
          style={{ width: 90 }}
          value={max}
          onChange={(e) => actualizar(min, e.target.value)}
        />
        <span className="muted" style={{ fontSize: 13 }}>dígitos</span>
      </div>
      <span className="hint">
        El NIC tendrá entre {min} y {max} dígitos.{" "}
        <a role="button" style={{ cursor: "pointer" }} onClick={() => setAvanzado(true)}>
          patrón avanzado
        </a>
        .
      </span>
    </div>
  );
}

function CamposPerfil({
  form,
  setForm,
}: {
  form: FormPerfil;
  setForm: (f: FormPerfil) => void;
}) {
  const [avanzado, setAvanzado] = useState(false);
  const set = (k: keyof FormPerfil, v: string | number) =>
    setForm({ ...form, [k]: v });

  return (
    <>
      <Campo
        label="Razón social"
        valor={form.nombre_legal}
        onChange={(v) => set("nombre_legal", v)}
        hint="Nombre legal completo del operador, tal como aparece en las cartas."
      />
      <div className="row" style={{ gap: 12 }}>
        <Campo label="NIT" valor={form.nit} onChange={(v) => set("nit", v)} />
        <Campo
          label="Código operador (CREG)"
          valor={form.codigo_operador}
          onChange={(v) => set("codigo_operador", v)}
          hint="Código del operador ante la CREG."
        />
      </div>
      <Campo
        label="Departamentos donde opera"
        valor={form.jurisdiccion}
        onChange={(v) => set("jurisdiccion", v)}
        hint="Separados por coma. Ej: Bolívar, Cesar, Córdoba."
      />
      <div className="row" style={{ gap: 12 }}>
        <Campo
          label="Aviso interno (días)"
          tipo="number"
          valor={String(form.umbral_dias_alerta_sap)}
          onChange={(v) => set("umbral_dias_alerta_sap", Number(v) || 0)}
          hint="Días hábiles tras los que el motor alerta antes del vencimiento."
        />
        <Campo
          label="Plazo legal (días)"
          tipo="number"
          valor={String(form.umbral_dias_legal_sap)}
          onChange={(v) => set("umbral_dias_legal_sap", Number(v) || 0)}
          hint="Plazo de ley (Art. 158 = 15 días hábiles). No cambiar salvo norma."
        />
      </div>
      <NicField valor={form.nic_regex} onChange={(v) => set("nic_regex", v)} />
      <div className="row" style={{ gap: 12 }}>
        <Campo label="Correo de soporte" valor={form.soporte_email} onChange={(v) => set("soporte_email", v)} />
        <Campo label="Página web" valor={form.web} onChange={(v) => set("web", v)} />
      </div>

      <button
        type="button"
        className="btn btn--ghost btn--sm"
        style={{ alignSelf: "flex-start" }}
        onClick={() => setAvanzado((v) => !v)}
      >
        {avanzado ? "▾ Ocultar configuración técnica" : "▸ Configuración técnica (avanzado)"}
      </button>
      {avanzado && (
        <div
          className="stack stack-3"
          style={{ borderLeft: "2px solid var(--line)", paddingLeft: 12 }}
        >
          <p className="hint" style={{ margin: 0 }}>
            Identificadores internos del sistema. Se autocompletan al crear la
            empresa; cámbialos solo si sabes lo que haces.
          </p>
          <Campo
            label="Namespace del corpus normativo"
            valor={form.corpus_namespace}
            onChange={(v) => set("corpus_namespace", v)}
            mono
            hint="Identificador del corpus (CCU) propio de la empresa en el RAG."
          />
          <div className="row" style={{ gap: 12 }}>
            <Campo label="Plantilla — general" valor={form.plantilla_default} onChange={(v) => set("plantilla_default", v)} mono />
            <Campo label="Plantilla — accede" valor={form.plantilla_accede} onChange={(v) => set("plantilla_accede", v)} mono />
          </div>
          <div className="row" style={{ gap: 12 }}>
            <Campo label="Plantilla — no accede" valor={form.plantilla_no_accede} onChange={(v) => set("plantilla_no_accede", v)} mono />
            <Campo label="Plantilla — improcedente" valor={form.plantilla_improcedente} onChange={(v) => set("plantilla_improcedente", v)} mono />
          </div>
        </div>
      )}
    </>
  );
}

function Campo({
  label,
  valor,
  onChange,
  tipo = "text",
  mono = false,
  hint,
}: {
  label: string;
  valor: string;
  onChange: (v: string) => void;
  tipo?: string;
  mono?: boolean;
  hint?: string;
}) {
  return (
    <div className="field" style={{ flex: 1 }}>
      <label>{label}</label>
      <input
        className={mono ? "input mono" : "input"}
        type={tipo}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

function BannerError({ texto }: { texto: string }) {
  return (
    <div className="banner banner--red">
      <span className="banner__icon">⚠</span>
      <div>{texto}</div>
    </div>
  );
}
